import { useEffect, useState, type FormEvent } from 'react'
import { apiClient, storeToken, hasToken } from '@/lib/api'

// Self-contained sign-in for Control Center. The app reads its token from
// localStorage (key `aios_jwt`) and the axios interceptor redirects here on 401,
// but nothing ever issued that token — so without this page there is no way in.
// POST /api/auth/login returns { accessToken }; we persist it and continue to the
// page the user was headed to (?next=) or /overview.
export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  // Already signed in → don't make them do it again.
  useEffect(() => {
    if (hasToken()) {
      const params = new URLSearchParams(window.location.search)
      window.location.replace(params.get('next') || '/overview')
    }
  }, [])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const res = await apiClient.post('/auth/login', { email, password })
      const token: string | undefined = res.data?.accessToken
      if (!token) throw new Error('No access token returned')
      storeToken(token)
      const params = new URLSearchParams(window.location.search)
      // Full navigation so every provider re-reads the now-authenticated token.
      window.location.href = params.get('next') || '/overview'
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Sign in failed')
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm space-y-5 rounded-lg border border-border bg-card p-8 shadow-sm"
      >
        <div className="space-y-1">
          <h1 className="text-xl font-semibold">MOS Control Center</h1>
          <p className="text-sm text-muted-foreground">Platform administration — sign in to continue.</p>
        </div>

        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="password" className="text-sm font-medium">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {error && (
          <p className="text-sm text-red-400" role="alert">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-opacity disabled:opacity-60"
        >
          {loading ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  )
}
